/**
 * DeclarationCard Component
 *
 * Single-checkbox combined declaration for the Payments section (same pattern
 * the guest checkout used): one tick covers Power of Attorney, the accuracy
 * declaration and the Terms/Privacy acceptance, and the signature is
 * auto-filled from the signed-in Clerk user's full name — no typed inputs.
 *
 * Server state via the existing declaration query/mutation hooks (TanStack
 * Query); ticking the box saves immediately. If no declaration record exists
 * (e.g. legacy guest declarations that were not migrated), it is simply
 * collected again here.
 */
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent
} from '../../../common';
import {
  AlertCircle,
  Pen,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { useSaveDeclarationMutation } from '../../../hooks/queries/declaration';

const DeclarationCard = ({ applicationId, declaration, isLoading, signerName }) => {
  const { t } = useTranslation('dashboard');
  const { t: tPayment } = useTranslation('payment');
  const saveDeclarationMutation = useSaveDeclarationMutation();

  const poaProvided = declaration?.poaProvided || false;
  const declarationProvided = declaration?.declarationProvided || false;
  const isCompleted = poaProvided && declarationProvided;
  const isSaving = saveDeclarationMutation.isPending;

  const handleToggle = useCallback(async (event) => {
    // The declaration is a signed record: it can only be granted here, never
    // un-granted by unticking.
    if (!event.target.checked || isCompleted || isSaving || !applicationId) return;

    const signature = (signerName || '').trim();
    const now = new Date().toISOString();
    try {
      await saveDeclarationMutation.mutateAsync({
        applicationId,
        data: {
          id: declaration?.id,
          // Power of Attorney
          poaProvided: true,
          poaSignature: signature,
          poaSignedAt: now,
          poaAcceptedTerms: true,
          // Declaration
          declarationProvided: true,
          declarationSignature: signature,
          declarationSignedAt: now,
          declarationAcceptedTerms: true,
        },
      });
    } catch (err) {
      console.error('Error saving declaration:', err);
    }
  }, [applicationId, declaration?.id, isCompleted, isSaving, saveDeclarationMutation, signerName]);

  if (isLoading) {
    return (
      <Card className="border border-gray-200">
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400 mr-3" />
          <span className="text-gray-500">{t('payments.declaration.loading', 'Loading declaration…')}</span>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={isCompleted ? 'border-2 border-emerald-300 bg-emerald-50/30' : 'border-2 border-gray-200'}>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Pen className="h-5 w-5 text-purple-600" />
          {t('payments.declaration.title', 'Authorisation & Declaration')}
          {isCompleted && <CheckCircle2 className="h-5 w-5 text-emerald-600 ml-auto" />}
        </CardTitle>
        {!isCompleted && (
          <CardDescription>
            {t('payments.declaration.subtitle', 'Required before we can file your trademark application on your behalf')}
          </CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-3">
        <label className={`flex items-start gap-3 py-1 ${isCompleted ? '' : 'cursor-pointer'}`}>
          {/* p-2 -m-2 grows the tap area around the checkbox to ~44px without
              shifting layout; the whole label is clickable too */}
          <span className="relative flex-shrink-0 p-2 -m-2 mt-0.5">
            <input
              type="checkbox"
              checked={isCompleted || isSaving}
              onChange={handleToggle}
              disabled={isCompleted || isSaving}
              className="h-6 w-6 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded disabled:opacity-70"
            />
            {isSaving && (
              <Loader2 className="absolute inset-0 m-auto h-4 w-4 animate-spin text-emerald-600" />
            )}
          </span>
          <span className="text-sm text-gray-700 leading-relaxed">
            {t('payments.declaration.combined', 'I confirm I am authorised to file this trade mark, that the information in this application is true and accurate, and I appoint MarkoTM as my agent to file and manage this application on my behalf. I accept the')}{' '}
            <a href="https://markotm.com/legal/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline">{tPayment('buttons.terms', 'Terms of Service')}</a>{' '}
            {tPayment('buttons.and', 'and')}{' '}
            <a href="https://markotm.com/legal/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-teal-700 hover:underline">{tPayment('buttons.privacy', 'Privacy Policy')}</a>.
          </span>
        </label>

        {isCompleted && (
          <div className="text-sm text-gray-700 pl-8">
            <span className="font-medium">{t('payments.declaration.signedBy', 'Signed by')}:</span>{' '}
            <span className="font-serif italic">{declaration?.declarationSignature || declaration?.poaSignature || signerName}</span>
            {declaration?.declarationSignedAt && (
              <span className="text-xs text-gray-500 ml-2">
                {t('payments.declaration.signedOn', 'on {{date}}', { date: new Date(declaration.declarationSignedAt).toLocaleDateString() })}
              </span>
            )}
          </div>
        )}

        {saveDeclarationMutation.isError && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700 text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>{t('payments.declaration.saveError', 'Could not save your declaration. Please try again.')}</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default DeclarationCard;
